/**
 * RequireAuth — the sign-in screen for anyone not signed in, at the landing URL.
 *
 * A visitor who is not signed in always ends up on the landing URL ('/'; '/cca/'
 * or '/eia/' locally) with the default sign-in screen shown there (the local
 * form's own /login address is a separate route, outside this guard — see
 * RequireSignedOut):
 *  - opening the landing URL itself shows it in place;
 *  - opening any other path (a deep link, a typed URL, an old bookmark) is
 *    REPLACED with the landing URL first, so the tab never shows the address of
 *    a page the visitor is not allowed to see. `replace`, so Back does not lead
 *    to it either.
 *
 * The page they asked for is not forgotten. It travels in the router's history
 * state (never in the address bar), and once they sign in on the local form
 * they are taken back to it rather than to the default section. Advisory SSO
 * leaves the app for Common Services and returns with a fresh page load, so
 * that state cannot survive it — those visitors land on the default section,
 * as in legacy.
 *
 * Which sign-in screen is the caller's choice (`fallback`); routes.jsx picks it
 * from config/loginMode.js. Keeping the guard ignorant of that means it stays a
 * guard, not a second router.
 *
 * Status handling, and why it is not simply "authenticated or not":
 *  - 'idle': auth has not been settled yet. bootstrapAuth settles it before the
 *    first render, so this is a safety net — nothing at all, not a flash of the
 *    sign-in screen (or a redirect) for someone whose cookies are about to
 *    restore them.
 *  - 'authenticating': the fallback STAYS. That status is set by the local form
 *    itself the moment Sign in is pressed; swapping the form for a spinner here
 *    would unmount it mid-request and throw away its state — including the
 *    error message a wrong password needs to show when the request fails.
 */
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';

import { selectAuthStatus, selectIsAuthenticated } from '@security/auth/authSlice';

/** The landing URL, relative to the router's basename. */
const LANDING = '/';

/**
 * @param {string} [afterSignIn] where a sign-in made ON this screen lands when
 *   no page was asked for first (legacy Login.vue -> Manage Portfolio). A
 *   visitor who arrives already signed in (cookies restored on load) is NOT
 *   moved: they get the landing page itself.
 */
export function RequireAuth({ children, fallback = null, afterSignIn }) {
  const location = useLocation();
  const status = useSelector(selectAuthStatus);
  const isAuthed = useSelector(selectIsAuthenticated);
  // Did this guard show the sign-in screen during this visit? Only then is a
  // switch to "signed in" a sign-in that just happened here.
  const showedSignIn = useRef(false);

  // Not settled yet. bootstrapAuth settles it before the first render, so this
  // is a safety net — and it shows nothing, never a "Loading…" page.
  if (status === 'idle') return null;

  if (!isAuthed) {
    if (location.pathname !== LANDING) {
      const { pathname, search, hash } = location;
      return <Navigate to={LANDING} replace state={{ from: { pathname, search, hash } }} />;
    }
    showedSignIn.current = true;
    return fallback;
  }

  // Just signed in on the landing URL after being sent there from a deep link:
  // go back to the page that was asked for, instead of the default section.
  const from = location.state?.from;
  if (location.pathname === LANDING && from?.pathname && from.pathname !== LANDING) {
    showedSignIn.current = false;
    return <Navigate to={from} replace />;
  }

  // Just signed in here with nothing asked for: the default section.
  if (showedSignIn.current && afterSignIn && location.pathname === LANDING) {
    showedSignIn.current = false;
    return <Navigate to={afterSignIn} replace />;
  }

  return children;
}
